<script setup>
import Card from "@/components/Tools/Card/Card.vue";
import FormHelper from "@/components/Tools/FormHelper/FormHelper.vue";
import { useReportExport } from "@/components/ExternalApps/TransferApp/ReportExports/composables/useReportExport";
import { computed, defineProps, defineEmits, onMounted, ref } from "vue";
import { useConfigValues } from "@/components/ExternalApps/TransferApp/composables/useConfigValues";
import Loader from "@/components/Tools/Loader.vue";
import { useNavigationEvent } from "@/components/ExternalApps/composables/useNavigationEvent";
import ProcessManager from "@/components/Workflows/processManager";
import { Toast } from "@/core/plugins/swal";
import i18n from "@/core/plugins/vue-i18n";

defineProps({
  saveButtonLabel: {
    type: String,
    required: true
  },
  mode: {
    type: String,
    required: true
  }
});

const emit = defineEmits(["save"]);

const { formValues } = useReportExport();
const { configValues, loadConfigValues } = useConfigValues();
const { navigate } = useNavigationEvent();

const configValuesOptions = computed(() =>
  configValues.value?.map(config => ({
    value: config.name,
    label: config.value.description
  }))
);

const form = computed(() => [
  {
    name: "reportId",
    type: "hidden"
  },
  {
    name: "description",
    type: "text",
    label: {
      de: "Bezeichnung",
      en: "Description"
    },
    validations: {
      required: true
    }
  },
  {
    name: "active",
    type: "checkbox",
    label: {
      de: "Aktivieren",
      en: "Activate"
    }
  },
  {
    name: "ftpConfig",
    type: "select",
    label: {
      de: "FTP-Konfiguration",
      en: "FTP configuration"
    },
    infoText: Object.values(configValuesOptions.value || {}).length
      ? ""
      : {
          de: "Du hast keine bestehenden FTP-Konfigurationen. Du kannst eine in den FTP-Einstellungen erstellen.",
          en: "You have no existing existing FTP-configurations. You can create one in the FTP settings"
        },
    infoTextAction: () => {
      const url = "/app/settings";
      const params = {
        menu: "administration",
        submenu: "connect-transfer"
      };
      const target = "_blank";

      navigate(url, params, target);
    },
    options: configValuesOptions.value,
    validations: {
      required: true
    }
  },
  {
    name: "interval",
    type: "select",
    label: {
      de: "Exporthäufigkeit",
      en: "Export frequency"
    },
    options: [
      {
        value: "monthly",
        label: {
          de: "Monatlich",
          en: "Monthly"
        }
      },
      {
        value: "weekly",
        label: {
          de: "Wöchentlich",
          en: "Weekly"
        }
      },
      {
        value: "daily",
        label: {
          de: "Täglich",
          en: "Daily"
        }
      }
    ],
    colClass: "col-12",
    validations: {
      required: true
    }
  },
  {
    name: "dayOfMonth",
    label: {
      de: "Tag des Monats",
      en: "Day of the month"
    },
    type: "number",
    colClass: "col-4",
    validations: {
      between: {
        min: 1,
        max: 31
      }
    },
    validationLabels: {
      between: {
        de: "Der Wert muss zwischen 1 und 31 liegen",
        en: "The value must be between 1 and 31."
      }
    },
    dependsOn: [
      {
        name: "interval",
        values: ["monthly"]
      }
    ]
  },
  {
    name: "weekday",
    label: {
      de: "Wochentage",
      en: "Days of the week"
    },
    type: "buttonGroup",
    colClass: "col-12",
    options: [
      {
        value: 1,
        label: {
          de: "Mo",
          en: "Mon"
        }
      },
      {
        value: 2,
        label: {
          de: "Di",
          en: "Tue"
        }
      },
      {
        value: 3,
        label: {
          de: "Mi",
          en: "Wed"
        }
      },
      {
        value: 4,
        label: {
          de: "Do",
          en: "Thu"
        }
      },
      {
        value: 5,
        label: {
          de: "Fr",
          en: "Fri"
        }
      },
      {
        value: 6,
        label: {
          de: "Sa",
          en: "Sat"
        }
      },
      {
        value: 0,
        label: {
          de: "So",
          en: "Sun"
        }
      }
    ],
    dependsOn: [
      {
        name: "interval",
        values: ["weekly"]
      }
    ]
  },
  {
    name: "startHour",
    label: {
      de: "Startstunde",
      en: "Start hour"
    },
    type: "number",
    colClass: "col-4",
    validations: {
      between: {
        min: 0,
        max: 23
      }
    },
    validationLabels: {
      between: {
        de: "Der Wert muss zwischen 0 und 23 liegen",
        en: "The value must be between 0 and 23."
      }
    },
    dependsOn: [
      {
        name: "interval",
        values: ["daily", "weekly", "monthly"]
      }
    ]
  },
  {
    name: "minuteOfHour",
    label: {
      de: "Minute der Stunde",
      en: "Minute of the hour"
    },
    type: "number",
    colClass: "col-4",
    validations: {
      between: {
        min: 0,
        max: 59
      }
    },
    validationLabels: {
      between: {
        de: "Der Wert muss zwischen 0 und 59 liegen",
        en: "The value must be between 0 and 59."
      }
    },
    dependsOn: [
      {
        name: "interval",
        values: ["daily", "weekly", "monthly"]
      }
    ]
  },
  {
    name: "path",
    type: "text",
    placeholder: {
      de: "Dateipfad",
      en: "File path"
    },
    label: {
      de: "Dateipfad",
      en: "File path"
    },
    validations: {
      required: true
    }
  },
  {
    name: "numberFormattingMode",
    type: "select",
    default: "raw",
    label: {
      de: "Zahlenformatierungsmodus",
      en: "Number Formatting Mode"
    },
    options: [
      {
        value: "raw"
      },
      {
        value: "local"
      }
    ],
    infoText: {
      de: "Legt fest, wie Zahlen bei der Anzeige formatiert werden (beeinflusst Exporte nicht).",
      en: "Number formatting mode that should be applied when visualizing numbers (does not affect exports)"
    }
  },
  {
    name: "filename",
    type: "text",
    placeholder: {
      de: "Dateiname",
      en: "File name"
    },
    label: {
      de: "Dateiname",
      en: "File name"
    },
    infoText: {
      de: "Variablen: {TIMESTAMP}, {DATUM}, Wenn leer, wird der Standardname verwendet.",
      en: "Variables: {TIMESTAMP}, {DATUM}, If empty, the default name is used."
    },
    validations: {
      required: true
    }
  },
  {
    name: "exportFormat",
    type: "select",
    label: {
      de: "Exportformat",
      en: "Export format"
    },
    colClass: "col-12",
    infoText: {
      de: "Lege das Dateiformat für deine Berichts-Exporte fest.",
      en: "Set the file format for your report exports."
    },
    options: [
      {
        value: "csv",
        label: "CSV"
      },
      {
        value: "json",
        label: "JSON"
      },
      {
        value: "parquet",
        label: "Parquet"
      },
      {
        value: "pdf",
        label: "PDF"
      },
      {
        value: "txt",
        label: "Text"
      },
      {
        value: "xlsx",
        label: "Excel"
      }
    ]
  },
  {
    name: "csvSeparator",
    type: "select",
    label: {
      de: "CSV-Trennzeichen",
      en: "CSV separator"
    },
    colClass: "col-12",
    infoText: {
      de: "Wähle das Trennzeichen, das für die Spaltentrennung in deinen exportierten Daten verwendet werden soll.",
      en: "Choose the separator used for column separation in your exported data."
    },
    options: [
      {
        value: ",",
        label: {
          de: "Komma ( , )",
          en: "Comma ( , )"
        }
      },
      {
        value: ";",
        label: {
          de: "Semikolon ( ; )",
          en: "Semicolon ( ; )"
        }
      },
      {
        value: "/",
        label: {
          de: "Schrägstrich ( / )",
          en: "Slash ( / )"
        }
      },
      {
        value: "|",
        label: {
          de: "Senkrechter Strich ( | )",
          en: "Pipe ( | )"
        }
      }
    ],
    validations: {
      required: true
    },
    dependsOn: [
      {
        name: "exportFormat",
        values: ["csv", "text"]
      }
    ]
  },
  {
    name: "escapeSpecialCharacters",
    type: "checkbox",
    label: {
      de: "Sonderzeichen maskieren",
      en: "Escape special characters"
    },
    dependsOn: [
      {
        name: "exportFormat",
        values: ["csv", "text"]
      }
    ]
  },
  {
    name: "disableQuotes",
    type: "checkbox",
    label: {
      de: "Anführungszeichen deaktivieren",
      en: "Disable quotes"
    },
    dependsOn: [
      {
        name: "exportFormat",
        values: ["csv", "text"]
      }
    ]
  }
]);

const formRef = ref();
const isMounted = ref(false);

const formConfig = {
  labelStacked: true
};

const isRunningExport = ref(false);

async function onRunExport() {
  isRunningExport.value = true;

  const ftpExportWorkflowId =
    process.env.VUE_APP_TRANSFER_APP_FTP_EXPORT_WORKFLOW_ID;
  try {
    await ProcessManager.run(ftpExportWorkflowId);

    await Toast.fire({
      icon: "success",
      title: i18n.t("reportExports.runExportSuccess")
    });
  } catch {
    await Toast.fire({
      icon: "error",
      title: i18n.t("reportExports.runExportError")
    });
  } finally {
    isRunningExport.value = false;
  }
}

function onSave() {
  const response = formRef.value.validate();

  if (response !== true) {
    return;
  }

  emit("save");
}

onMounted(async () => {
  await loadConfigValues();
  isMounted.value = true;
});
</script>

<template>
  <Card class="ftp-card pa-6">
    <Loader v-if="!isMounted" />
    <template v-else>
      <FormHelper
        ref="formRef"
        v-model="formValues"
        class="report-exports-form"
        :form="form"
        :config="formConfig"
      />

      <div class="ftp-card__actions">
        <button class="btn btn-primary" @click="onSave">
          {{ saveButtonLabel }}
        </button>
        <button
          v-if="mode === 'edit'"
          class="btn btn-light ml-3"
          :disabled="isRunningExport"
          @click="onRunExport"
        >
          {{ $t("reportExports.runExport") }}
        </button>
      </div>
    </template>
  </Card>
</template>

<style scoped lang="scss">
:deep(.form-helper) {
  .v-picker {
    max-width: none;
  }

  .form-field {
    .col-form-label {
      padding: 0;
    }

    .label-wrapper {
      display: grid;
      gap: 2px;
      margin-bottom: 4px;
    }
  }
}

.ftp-container {
  display: flex;
  place-items: center;
}

.ftp-card {
  width: 100%;
  height: 100%;
  border-radius: 0;
  box-shadow: none;
  border: none;
  display: grid;
  gap: 16px;
  grid-template-rows: 1fr auto;
  align-content: space-between;

  .ftp-button {
    position: sticky;
    top: 0;
    width: 100px;
    right: 0;
    align-self: flex-end;
    z-index: 100;
  }
}

.report-exports-form {
  max-width: 45rem;
}
</style>
